import { makeEsArticle, type EsArticleInput } from "./learnFactoryEs";
import type { LearnFAQ } from "@/pages/learn/learnShared";

/**
 * /es/learn/camera-guided-portable-tms
 */

const AMPA_TRADEMARK =
  "Ampa y Ampa One son marcas registradas de Ampa Inc. Neuro Wellness TMS Centers of America es un proveedor independiente y no pertenece a Ampa.";

const faqs: LearnFAQ[] = [
  {
    question: "¿Qué es la neuronavegación por TMS?",
    answer:
      "La neuronavegación por TMS es cualquier método que sigue dónde está la bobina en su cabeza para que el tratamiento permanezca en el mismo objetivo sesión tras sesión. La neuronavegación guiada por cámara usa una cámara (en la bobina o en la sala) más un gorro de referencia para seguir la posición de la bobina en vivo. Es más precisa que las mediciones manuales de la cabeza y es diferente de la orientación guiada por resonancia, que usa una resonancia cerebral para elegir el objetivo en primer lugar.",
  },
  {
    question: "¿Hay TMS portátil que pueda usar en casa?",
    answer:
      "No. Ampa One es portátil para la clínica: cabe en dos maletas con ruedas y funciona con un enchufe estándar, pero se usa en la clínica bajo la supervisión de un técnico capacitado. No es un dispositivo para uso en casa. El TMS requiere mapeo del umbral motor, supervisión médica y evaluación de seguridad, lo cual no se puede hacer en casa.",
  },
  {
    question: "¿Cuánto dura una sesión de TMS con Ampa One?",
    answer:
      "Una sesión de Ampa One son unos minutos de estimulación: aproximadamente un minuto de preparación y luego unos minutos de tratamiento. Eso es mucho más corto que los 19–37 minutos de una sesión de TMS convencional, porque Ampa One usa patrones de estimulación de ráfaga theta. Puede conducir a casa y volver al trabajo después.",
  },
  {
    question: "¿Qué es una sesión de TMS de 3 minutos?",
    answer:
      "Una 'sesión de TMS de 3 minutos' se refiere a la estimulación de ráfaga theta (TBS), que entrega el mismo número de pulsos que una sesión convencional más larga en una fracción del tiempo usando un patrón de ráfaga más rápido. Ampa One usa TBS, por lo que una sesión de estimulación puede tomar solo unos minutos. La sesión corta es una característica del patrón de estimulación, no una dosis menor.",
  },
];

export const esCameraGuidedPortableTms = makeEsArticle({
  slug: "camera-guided-portable-tms",
  title:
    "TMS guiado por cámara y portátil: cómo la neuronavegación mantiene el tratamiento en el objetivo",
  description:
    "Qué es el TMS guiado por cámara, cómo la neuronavegación mantiene la bobina en el objetivo, qué significa el TMS 'portátil' y cómo funcionan las sesiones de 3 minutos con ráfaga theta. Ampa One es el sistema que usamos.",
  h1: "¿Qué es el TMS guiado por cámara? Portátil, preciso, sesiones de minutos",
  breadcrumbLabel: "TMS guiado por cámara y portátil",
  shortAnswer:
    "El TMS guiado por cámara usa una cámara en la bobina y un gorro preimpreso para seguir dónde está la bobina en su cabeza durante cada sesión, para que el tratamiento permanezca en el mismo objetivo día tras día. Es diferente de la orientación guiada por resonancia (usada por SAINT®), que usa una resonancia cerebral para elegir un objetivo personalizado. Ampa One es el sistema de TMS guiado por cámara y portátil que usamos. Los resultados individuales varían. No se garantiza ningún resultado. La cobertura varía según el plan. Ampa One está disponible con receta tras una evaluación clínica.",
  keyTakeaways: [
    "El TMS guiado por cámara sigue la posición de la bobina en vivo con una cámara más un gorro preimpreso.",
    "Es diferente de la orientación guiada por resonancia (SAINT®), que usa una resonancia para elegir el objetivo.",
    "'Portátil' significa portátil para la clínica (cabe en dos maletas, funciona con un enchufe estándar), no un dispositivo para casa.",
    "La estimulación de ráfaga theta (TBS) hace que las sesiones duren minutos en lugar de 19–37 minutos.",
    "Ampa One es el sistema de TMS guiado por cámara y portátil que usamos.",
  ],
  sections: [
    {
      heading: "Respuesta rápida",
      paragraphs: [
        "El TMS guiado por cámara usa una cámara integrada en la bobina y un gorro de neuronavegación preimpreso para seguir dónde está la bobina en su cabeza durante cada sesión, para que el tratamiento permanezca en el mismo objetivo día tras día. Es diferente de la orientación guiada por resonancia (usada por SAINT®), que usa una resonancia cerebral para elegir un objetivo personalizado. Ampa One es el sistema de TMS guiado por cámara y portátil que usamos.",
      ],
    },
    {
      heading: "Tres formas en que el TMS encuentra su objetivo",
      paragraphs: [
        "Los sistemas de TMS encuentran su objetivo de tres formas principales, de la más simple a la más personalizada:",
      ],
      table: {
        headers: ["Método", "Cómo funciona", "Ejemplo"],
        rows: [
          [
            "Mediciones de la cabeza",
            "Mediciones manuales desde puntos de referencia de su cráneo para estimar el objetivo",
            "TMS convencional (NeuroStar, MagVenture)",
          ],
          [
            "Neuronavegación por cámara",
            "Una cámara más un gorro preimpreso sigue la posición de la bobina en vivo cada sesión",
            "Ampa One",
          ],
          [
            "Orientación por resonancia / fMRI",
            "Una resonancia cerebral personaliza el objetivo a su anatomía cerebral individual",
            "SAINT® (fMRI)",
          ],
        ],
      },
    },
    {
      heading: "Por qué importa la deriva de la bobina",
      paragraphs: [
        "Los pacientes se mueven durante el tratamiento y las bobinas se desplazan. Con mediciones manuales, la bobina puede desviarse del objetivo original a lo largo de un curso de varias semanas, lo que puede reducir la estimulación que llega al área prevista. La neuronavegación por cámara sigue la bobina en vivo, y el Target Lock de Ampa One (introducido en octubre de 2026) pausa automáticamente la estimulación si la bobina se sale del objetivo y ayuda al técnico a realinearla, para que cada sesión permanezca en el mismo objetivo.",
      ],
    },
    {
      heading: "TMS portátil: qué significa 'portátil'",
      paragraphs: [
        "Ampa One es portátil para la clínica: cabe en dos maletas con ruedas y funciona con un enchufe estándar, por lo que se puede instalar en una sala de exploración sin el brazo mecánico pesado de un sistema convencional. No es un dispositivo para uso en casa: el TMS requiere mapeo del umbral motor, supervisión médica y evaluación de seguridad, que se hacen en la clínica bajo supervisión.",
      ],
    },
    {
      heading: "De sesiones de 38 minutos a sesiones de 3 minutos",
      paragraphs: [
        "Las sesiones de TMS convencional duran 19–37 minutos porque entregan pulsos uno a uno a 10 Hz. La estimulación de ráfaga theta (TBS) entrega el mismo número de pulsos en una fracción del tiempo usando un patrón de ráfaga más rápido, por lo que una sesión de estimulación de Ampa One puede tomar solo unos minutos. La sesión corta es una característica del patrón de estimulación, no una dosis menor. Consulte [[TMS acelerado y ráfaga theta|/es/learn/accelerated-tms-theta-burst-stimulation]].",
      ],
    },
    {
      heading:
        "¿Es el TMS guiado por cámara tan bueno como el guiado por resonancia?",
      paragraphs: [
        "Resuelven problemas diferentes. La orientación guiada por resonancia (SAINT®) usa una resonancia cerebral para elegir un objetivo personalizado, lo cual es más complejo y requiere una resonancia. La orientación guiada por cámara (Ampa One) mantiene la bobina en un objetivo elegido sesión tras sesión, con sesiones cortas y sin necesidad de resonancia, y admite programas acelerados. Ningún ensayo cara a cara los compara. Si no puede hacerse una resonancia, el TMS guiado por cámara puede ser la mejor opción. Consulte [[SAINT® vs Ampa One|/es/learn/saint-vs-ampa-one]].",
      ],
    },
  ],
  faqs,
  sources: [
    {
      citation:
        "Ampa Inc. Ampa One — sistema de TMS guiado por cámara y portátil; Target Lock (octubre de 2026).",
      url: "https://www.ampahealth.com/",
    },
  ],
  relatedLinks: [
    {
      title: "TMS Ampa One™ en Florida",
      href: "/es/ampa",
      description: "Tratamiento de depresión acelerado y guiado por cámara",
    },
    {
      title: "SAINT® vs Ampa One",
      href: "/es/learn/saint-vs-ampa-one",
      description: "Orientación guiada por cámara vs guiada por fMRI",
    },
    {
      title: "Ampa One vs NeuroStar vs BrainsWay",
      href: "/es/learn/ampa-one-vs-neurostar-vs-brainsway",
      description: "Compare dispositivos de TMS",
    },
    {
      title: "TMS acelerado y ráfaga theta",
      href: "/es/learn/accelerated-tms-theta-burst-stimulation",
      description: "Cómo la ráfaga theta acorta las sesiones",
    },
    {
      title: "Solicitar una consulta",
      href: "/es/contactus",
      description: "Reserve una consulta gratuita con nuestro equipo clínico",
    },
  ],
  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    reviewedByHref: "/dr-daniel-pietras-md",
    published: "3 de noviembre de 2026",
    lastReviewed: "3 de noviembre de 2026",
    lastUpdated: "3 de noviembre de 2026",
  },
  toc: [
    { label: "Respuesta rápida", href: "#respuesta-rapida" },
    {
      label: "Tres formas de orientar",
      href: "#tres-formas-en-que-el-tms-encuentra-su-objetivo",
    },
    {
      label: "Deriva de la bobina",
      href: "#por-que-importa-la-deriva-de-la-bobina",
    },
    { label: "TMS portátil", href: "#tms-portatil-que-significa-portatil" },
    {
      label: "Sesiones de 3 minutos",
      href: "#de-sesiones-de-38-minutos-a-sesiones-de-3-minutos",
    },
    {
      label: "Cámara vs resonancia",
      href: "#es-el-tms-guiado-por-camara-tan-bueno-como-el-guiado-por-resonancia",
    },
    { label: "Preguntas frecuentes", href: "#faq" },
    { label: "Investigación citada", href: "#cited-research" },
  ],
  safetyBlock:
    "Los resultados individuales varían. No se garantiza ningún resultado. La cobertura varía según el plan. Ampa One está disponible con receta tras una evaluación clínica. Los efectos secundarios comunes incluyen molestias leves en el cuero cabelludo y dolor de cabeza que suelen desaparecer. Si está en crisis, llame o envíe un mensaje de texto al 988.",
  trademarkFooter: AMPA_TRADEMARK,
  ctaBand: {
    headline:
      "Vea si el TMS guiado por cámara es adecuado para usted, sin costo",
    primaryText: "Solicitar una consulta gratuita",
    primaryHref: "/es/ampa",
    secondaryText: "Llamar 954-827-9793",
    secondaryHref: "tel:9548279793",
    phone: "954-827-9793",
  },
} as EsArticleInput);
